/**
 * 🎨 ReferralLinkCard - Referral Link Management
 * 
 * Features:
 * - Link Display mit Copy-Button
 * - QR-Code anzeigen
 * - Social Share Buttons
 * - Link-Statistiken
 */

import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { 
  Link2, 
  Copy, 
  Check, 
  QrCode, 
  ExternalLink,
  MousePointerClick,
  Users,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// ============================================
// TYPES
// ============================================

export type SharePlatform = 'whatsapp' | 'telegram' | 'twitter' | 'facebook' | 'email' | 'linkedin';

export interface LinkStats {
  clicks: number;
  conversions: number;
  conversionRate: number;
}

export interface ReferralLinkCardProps {
  /** Der vollständige Referral Link */
  link: string;
  /** Kurz-Code für Anzeige */
  shortCode: string;
  /** Callback beim Kopieren */
  onCopy: () => void;
  /** Callback für QR-Code Anzeige */
  onShowQR: () => void;
  /** Callback für Social Share */
  onShare: (platform: SharePlatform) => void;
  /** Statistiken zum Link */
  stats?: LinkStats;
  /** Loading State */
  isLoading?: boolean;
  /** Zusätzliche CSS Klassen */
  className?: string;
}

// ============================================
// CONSTANTS
// ============================================

const SHARE_PLATFORMS: Array<{
  id: SharePlatform;
  label: string;
  icon: string;
  color: string;
}> = [
  { id: 'whatsapp', label: 'WhatsApp', icon: '💬', color: 'hover:bg-green-50 hover:text-green-600' },
  { id: 'telegram', label: 'Telegram', icon: '✈️', color: 'hover:bg-blue-50 hover:text-blue-600' },
  { id: 'twitter', label: 'Twitter/X', icon: '🐦', color: 'hover:bg-sky-50 hover:text-sky-600' },
  { id: 'facebook', label: 'Facebook', icon: '📘', color: 'hover:bg-blue-50 hover:text-blue-700' },
  { id: 'linkedin', label: 'LinkedIn', icon: '💼', color: 'hover:bg-blue-50 hover:text-blue-800' },
  { id: 'email', label: 'E-Mail', icon: '📧', color: 'hover:bg-gray-50 hover:text-gray-700' },
];

// ============================================
// MAIN COMPONENT
// ============================================

export const ReferralLinkCard: React.FC<ReferralLinkCardProps> = ({
  link,
  shortCode,
  onCopy,
  onShowQR,
  onShare,
  stats,
  className,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      onCopy();
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Formatiere Zahlen
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('de-DE').format(num);
  };

  const formatPercent = (num: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(num / 100);
  };

  return (
    <TooltipProvider>
      <div className={cn(
        'bg-white/80 backdrop-blur-xl rounded-2xl p-4 sm:p-5 md:p-6',
        'shadow-[0_4px_24px_rgba(0,0,0,0.06)]',
        'border border-white/60',
        'relative overflow-hidden',
        className
      )}>
        {/* Gradient Accent */}
        <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-gradient-to-bl from-emerald-500/10 to-transparent rounded-bl-full" />
        
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 sm:mb-5 relative">
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/25 shrink-0">
            <Link2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-sm sm:text-base bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">Dein Empfehlungslink</h3>
            <p className="text-xs sm:text-sm text-gray-500 truncate">Teile diesen Link</p>
          </div>
        </div>

        {/* Link Input Field */}
        <div className="flex flex-col gap-2 mb-4 sm:mb-5 relative">
          {/* Full link display — scrollable, no truncation */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={cn(
                'flex items-center px-3 sm:px-4 h-auto min-h-[44px] sm:min-h-[48px] py-2.5 rounded-xl',
                'bg-white/60 border border-gray-200/50',
                'text-gray-700 font-mono text-xs sm:text-sm',
                'backdrop-blur-sm cursor-pointer',
                'hover:border-emerald-200 hover:bg-emerald-50/30 transition-colors'
              )}
              onClick={handleCopy}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleCopy()}
            >
                <span className="break-all select-all leading-relaxed">{link}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {copied ? '✅ Kopiert!' : 'Klicken zum Kopieren'}
            </TooltipContent>
          </Tooltip>

          {/* Action buttons row */}
          <div className="flex gap-2">
            {/* Copy Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'h-10 flex-1 sm:flex-none sm:h-11 sm:px-4 gap-2 rounded-xl',
                    'border-gray-200/50 bg-white/60 backdrop-blur-sm',
                    'hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-600',
                    'transition-colors duration-200 active:scale-95',
                    copied && 'bg-emerald-50 border-emerald-200 text-emerald-600'
                  )}
                  onClick={handleCopy}
                >
                  {copied ? (
                    <><Check className="w-4 h-4" /><span className="text-xs font-medium">Kopiert!</span></>
                  ) : (
                    <><Copy className="w-4 h-4" /><span className="text-xs font-medium">Kopieren</span></>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {copied ? 'Kopiert!' : 'Link kopieren'}
              </TooltipContent>
            </Tooltip>

            {/* QR Code Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className="h-10 sm:h-11 px-3 sm:px-4 gap-2 rounded-xl border-gray-200/50 bg-white/60 backdrop-blur-sm hover:bg-purple-50 hover:border-purple-200 hover:text-purple-600 transition-colors duration-200 active:scale-95"
                  onClick={onShowQR}
                >
                  <QrCode className="w-4 h-4" />
                  <span className="text-xs font-medium hidden xs:inline">QR-Code</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                QR-Code anzeigen
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Share Section - Mobile optimized */}
        <div className="mb-4 sm:mb-5 relative">
          <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">Schnell teilen:</p>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {SHARE_PLATFORMS.slice(0, 4).map((platform) => (
              <button
                key={platform.id}
                onClick={() => onShare(platform.id)}
                className={cn(
                  'inline-flex items-center justify-center gap-1.5 sm:gap-2',
                  'min-h-[44px] px-3 sm:px-4 py-2 rounded-xl',
                  'text-sm font-medium text-gray-600',
                  'bg-white/60 backdrop-blur-sm border border-gray-200/50',
                  'transition-colors duration-200 active:scale-95',
                  platform.color
                )}
              >
                <span className="text-base">{platform.icon}</span>
                <span className="hidden xs:inline sm:inline">{platform.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Stats Section - Mobile optimized */}
        {stats && (
          <div className={cn(
            'pt-4 sm:pt-5 border-t border-gray-100/50',
            'grid grid-cols-3 gap-2 sm:gap-3'
          )}>
            <div className="text-center px-1.5 sm:px-2 py-2 sm:py-3 rounded-xl bg-gradient-to-br from-blue-50/80 to-white/50">
              <div className="flex items-center justify-center gap-1 text-blue-500 mb-0.5 sm:mb-1">
                <MousePointerClick className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs font-medium">Klicks</span>
              </div>
              <p className="text-lg sm:text-xl font-bold bg-gradient-to-br from-blue-700 to-blue-500 bg-clip-text text-transparent">
                {formatNumber(stats.clicks)}
              </p>
            </div>
            
            <div className="text-center px-1.5 sm:px-2 py-2 sm:py-3 rounded-xl bg-gradient-to-br from-purple-50/80 to-white/50">
              <div className="flex items-center justify-center gap-1 text-purple-500 mb-0.5 sm:mb-1">
                <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs font-medium">Conv.</span>
              </div>
              <p className="text-lg sm:text-xl font-bold bg-gradient-to-br from-purple-700 to-purple-500 bg-clip-text text-transparent">
                {formatNumber(stats.conversions)}
              </p>
            </div>
            
            <div className="text-center px-1.5 sm:px-2 py-2 sm:py-3 rounded-xl bg-gradient-to-br from-emerald-50/80 to-white/50">
              <div className="flex items-center justify-center gap-1 text-emerald-500 mb-0.5 sm:mb-1">
                <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="text-[10px] sm:text-xs font-medium">Rate</span>
              </div>
              <p className="text-lg sm:text-xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-500 bg-clip-text text-transparent">
                {formatPercent(stats.conversionRate)}
              </p>
            </div>
          </div>
        )}

        {/* Short Code Display - Mobile optimized */}
        <div className="mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-gray-100/50 flex flex-col xs:flex-row sm:flex-row items-start xs:items-center sm:items-center justify-between gap-2 sm:gap-0 relative">
          <div className="text-sm">
            <span className="text-gray-500">Code: </span>
            <code className="px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-gray-100 to-gray-50 rounded-lg font-mono font-semibold text-gray-800 border border-gray-200/50 text-xs sm:text-sm">
              {shortCode}
            </code>
          </div>
          
          <a 
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex items-center gap-1.5 text-sm font-medium py-2 px-1 min-h-[44px]',
              'text-emerald-600 hover:text-emerald-700 active:text-emerald-800',
              'transition-colors duration-200'
            )}
          >
            <span>Vorschau</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ReferralLinkCard;
